// Dreiwege-Zusammenführung für paralleles Bearbeiten derselben Datendatei.
// base = Stand, den ich zuletzt aus der Datei gelesen bzw. hineingeschrieben habe
// mine = mein aktueller Stand, theirs = was jetzt in der Datei steht (von anderen gespeichert).
// Zusammengeführt wird je Element (über die id) und darin je Feld. Ändern zwei Personen
// dasselbe Feld unterschiedlich, gewinnt meine Fassung; das wird als Konflikt gemeldet.

const COLLECTIONS = ['lanes', 'systems', 'edgeTypes', 'processes', 'nodes', 'edges', 'decisions'];
const KIND = { lanes: 'Bahn', systems: 'System', edgeTypes: 'Verbindungsart', processes: 'Prozess', nodes: 'Schritt', edges: 'Verbindung', decisions: 'Entscheidung' };

const isObj = (x) => x && typeof x === 'object' && !Array.isArray(x);

export function same(a, b) {
  if (a === b) return true;
  if (typeof a !== typeof b || !a || !b || typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) return a.length === b.length && a.every((x, i) => same(x, b[i]));
  const ka = Object.keys(a).filter((k) => a[k] !== undefined);
  const kb = Object.keys(b).filter((k) => b[k] !== undefined);
  return ka.length === kb.length && ka.every((k) => same(a[k], b[k]));
}

/** Wert zusammenführen; Objekte feldweise. path dient nur der Konfliktmeldung. */
function mergeValue(b, m, t, path, conflicts) {
  if (same(m, b)) return t;
  if (same(t, b) || same(m, t)) return m;
  if (isObj(b) && isObj(m) && isObj(t)) {
    const out = {};
    for (const k of new Set([...Object.keys(t), ...Object.keys(m), ...Object.keys(b)])) {
      const v = mergeValue(b[k], m[k], t[k], path + '.' + k, conflicts);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }
  conflicts.push(path);
  return m;
}

const label = (coll, x) => `${KIND[coll]} „${x.titel || x.name || x.frage || x.id}“`;

function mergeCollection(coll, b = [], m = [], t = [], conflicts, notes) {
  const B = new Map(b.map((x) => [x.id, x]));
  const M = new Map(m.map((x) => [x.id, x]));
  const T = new Map(t.map((x) => [x.id, x]));
  const out = [];
  const take = (id) => {
    const bx = B.get(id), mx = M.get(id), tx = T.get(id);
    if (bx) {
      if (!mx && !tx) return null;
      if (!mx) { // von mir gelöscht
        if (same(tx, bx)) return null;
        notes.push(`${label(coll, tx)} wurde von mir gelöscht, aber von jemand anderem geändert und bleibt erhalten.`);
        return tx;
      }
      if (!tx) { // von anderen gelöscht
        if (same(mx, bx)) return null;
        notes.push(`${label(coll, mx)} wurde von jemand anderem gelöscht, aber von mir geändert und bleibt erhalten.`);
        return mx;
      }
      const c0 = [];
      const v = mergeValue(bx, mx, tx, '', c0);
      const c = c0.filter((p) => !/\.geaendert(Von|Am)$/.test(p));
      if (c.length) conflicts.push(`${label(coll, mx)}: ${c.map((p) => p.slice(1)).join(', ')}`);
      return v;
    }
    if (mx && tx && !same(mx, tx)) conflicts.push(`${label(coll, mx)} (von beiden neu angelegt)`);
    return mx || tx;
  };
  // Reihenfolge der Datei beibehalten, eigene neue Elemente hinten anfügen
  const seen = new Set();
  for (const id of [...T.keys(), ...M.keys()]) {
    if (seen.has(id)) continue;
    seen.add(id);
    const v = take(id);
    if (v) out.push(v);
  }
  return out;
}

/** Gibt { data, conflicts, notes } zurück. */
export function merge3(base, mine, theirs) {
  const conflicts = [];
  const notes = [];
  const data = { ...theirs, ...mine };
  data.schemaVersion = Math.max(mine.schemaVersion || 1, theirs.schemaVersion || 1);
  const mc = [];
  // Bearbeiter-Vermerk, Fassungskennungen und Version zählen nicht als Konflikt;
  // Version, Stand und Verlauf kommen aus der Datei und werden beim Speichern fortgeschrieben.
  const strip = (x) => { const { geaendertVon, geaendertAm, revs, version, stand, ...r } = x || {}; return r; };
  data.meta = {
    ...mergeValue(strip(base.meta), strip(mine.meta), strip(theirs.meta), '', mc),
    geaendertVon: mine.meta?.geaendertVon, geaendertAm: mine.meta?.geaendertAm, revs: mine.meta?.revs,
    version: theirs.meta?.version ?? mine.meta?.version, stand: theirs.meta?.stand ?? mine.meta?.stand,
  };
  data.history = theirs.history ?? mine.history ?? [];
  if (mc.length) conflicts.push('Einstellungen: ' + mc.map((p) => p.slice(1)).join(', '));
  for (const k of COLLECTIONS) data[k] = mergeCollection(k, base[k], mine[k], theirs[k], conflicts, notes);
  // Verbindungen ohne Schritte (z. B. Schritt von anderen gelöscht) entfernen
  const nodeIds = new Set(data.nodes.map((n) => n.id));
  const before = data.edges.length;
  data.edges = data.edges.filter((e) => nodeIds.has(e.from) && nodeIds.has(e.to));
  if (data.edges.length < before) notes.push(`${before - data.edges.length} Verbindung(en) zu gelöschten Schritten entfernt.`);
  return { data, conflicts, notes };
}
